import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const FIELD_TYPE_LABELS = {
  text: 'Textbox',
  multi_choice: 'Multiple Choice',
  checkbox: 'Multi Checkbox',
  select: 'Dropdown',
  date: 'Date',
  number: 'Number',
};

const PREDEFINED_TYPE_LABELS = {
  priority: 'Dropdown',
  allocated_hours: 'Number',
  time_tracking: 'Time Tracking',
  hours_spent: 'Number',
  assignee: 'People',
  collaborators: 'People',
  label: 'Tags',
  milestone: 'Milestone',
  due_date: 'Date',
  reporter: 'People',
};

const toTitleCase = (value = '') =>
  value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const isCustomField = (field) => field?.field_type === 'custom';

const getFieldTypeLabel = (field) => {
  if (field?.type) {
    return FIELD_TYPE_LABELS[field.type] || toTitleCase(field.type);
  }

  if (field?.field_type === 'predefined') {
    return PREDEFINED_TYPE_LABELS[field.display_name] || FIELD_TYPE_LABELS.predefined;
  }

  return FIELD_TYPE_LABELS[field?.field_type] || 'Field';
};

const getFieldBadgeLabel = (field) => (isCustomField(field) ? 'Custom' : 'Default');

const ManageFieldsRight = ({
  fields,
  onDragEnd,
  dragAndDropIcon,
  handleDeleteCustomField,
  handleToggleCustomFieldVisibility,
  isUpdatingVisibility,
  onEditField,
}) => (
  <Box className="manage-fields-right">
    <Box className="manage-fields-drag-list">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="fields-droppable">
          {(droppableProvided) => (
            <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
              {fields.map((item, idx) => {
                const customField = isCustomField(item);
                const hiddenField = Boolean(item.hide_field);
                const fieldTypeLabel = getFieldTypeLabel(item);

                return (
                  <Draggable key={item.id} draggableId={item.id} index={idx}>
                    {(draggableProvided, snapshot) => (
                      <Box
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        className={`manage-fields-drag-item${snapshot.isDragging ? ' dragging' : ''}${hiddenField ? ' is-hidden' : ''}`}
                      >
                            {customField && (
                              <Tooltip title="Delete field">
                                <IconButton
                                  className="manage-fields-delete-floating"
                                  onClick={() => handleDeleteCustomField(item)}
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                          </Tooltip>
                        )}

                        <Box className="manage-fields-item-main">
                          <Box className="manage-fields-item-row">
                            <Box className="manage-fields-item-title-wrap">
                              <span {...draggableProvided.dragHandleProps} className="manage-fields-drag-handle">
                                <img src={dragAndDropIcon} alt="drag" />
                              </span>
                              <Box className="manage-fields-item-copy">
                                <Typography className="manage-fields-drag-label">
                                  {toTitleCase(item.name)}
                                </Typography>
                                <Typography className="manage-fields-field-type">
                                  {fieldTypeLabel}
                                </Typography>
                              </Box>
                            </Box>

                            <Box className="manage-fields-item-actions">
                              {customField && (
                                <Box className="manage-fields-inline-icons">
                                  <Tooltip title="Editable">
                                    <IconButton
                                      className="manage-fields-inline-icon"
                                      onClick={() => onEditField(idx)}
                                      size="small"
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={hiddenField ? 'Show field' : 'Hide field'}>
                                    <IconButton
                                      className="manage-fields-inline-icon"
                                      onClick={() => handleToggleCustomFieldVisibility(item)}
                                      size="small"
                                      disabled={isUpdatingVisibility}
                                    >
                                      {!hiddenField ? (
                                        <VisibilityOutlinedIcon fontSize="small" />
                                      ) : (
                                        <VisibilityOffOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}

                              <Box className={`manage-fields-badge ${customField ? 'is-custom' : 'is-default'}`}>
                                {getFieldBadgeLabel(item)}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  </Box>
);

export default ManageFieldsRight;
